import { useEffect, useState } from "react";
import { toast } from "sonner";
import { HardHat, Plus, Trash2, Loader2, Pencil } from "lucide-react";
import api, { apiError, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

export default function Trades() {
  const { isAdmin } = useAuth();
  const [trades, setTrades] = useState([]);
  const [name, setName] = useState("");
  const [adding, setAdding] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [toEdit, setToEdit] = useState(null);

  const load = () => api.get("/trades").then((r) => setTrades(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name.trim()) return;
    setAdding(true);
    try {
      await api.post("/trades", { name: name.trim() });
      setName("");
      load();
      toast.success("İş kalemi eklendi");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const del = async () => {
    try {
      await api.delete(`/trades/${toDelete.id}`);
      toast.success("Silindi");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setToDelete(null);
    }
  };

  return (
    <div className="mx-auto max-w-3xl animate-fade-up">
      <PageHeader overline="Kategoriler" title="İş Kalemleri" subtitle="Giderleri gruplamak için iş kalemleri" />

      <div className="mb-4 flex gap-2 rounded-md border border-border bg-card p-4">
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Yeni iş kalemi adı" data-testid="trade-name-input" />
        <Button onClick={add} disabled={adding} data-testid="add-trade-button">
          {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" /> Ekle</>}
        </Button>
      </div>

      <div className="divide-y divide-border rounded-md border border-border bg-card" data-testid="trades-list">
        {trades.map((t) => (
          <div key={t.id} className="flex items-center justify-between px-4 py-3" data-testid={`trade-${t.id}`}>
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary"><HardHat className="h-4 w-4 text-slate-500" /></div>
              <div>
                <p className="text-sm font-medium">{t.name}</p>
                <p className="text-xs text-muted-foreground">{t.created_by} · {fmtDate(t.created_at)}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setToEdit(t)} data-testid={`edit-trade-${t.id}`}>
                <Pencil className="h-4 w-4" />
              </Button>
              {isAdmin && (
                <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setToDelete(t)} data-testid={`delete-trade-${t.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
        {trades.length === 0 && <div className="py-12 text-center text-sm text-muted-foreground">Henüz iş kalemi yok</div>}
      </div>

      {toEdit && <EditTradeDialog trade={toEdit} onClose={() => setToEdit(null)} onSaved={() => { setToEdit(null); load(); }} />}

      <AlertDialog open={!!toDelete} onOpenChange={() => setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>İş kalemini sil?</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.name}" silinecek. Bu iş kalemi giderlerde kullanılıyorsa silinemez.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); del(); }} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-delete-trade">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EditTradeDialog({ trade, onClose, onSaved }) {
  const [name, setName] = useState(trade.name);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error("İsim boş olamaz"); return; }
    if (!comment.trim()) { toast.error("Değişiklik yorumu zorunludur"); return; }
    setSaving(true);
    try {
      await api.put(`/trades/${trade.id}`, { name: name.trim(), comment: comment.trim() });
      toast.success("İş kalemi güncellendi");
      onSaved();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="edit-trade-dialog">
        <DialogHeader>
          <DialogTitle>İş Kalemini Düzenle</DialogTitle>
          <DialogDescription>İsim değişikliği için zorunlu bir yorum giriniz. Eski ve yeni değer geçmişe kaydedilir.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>İsim</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-trade-name-input" />
          </div>
          <div className="space-y-1.5 rounded-md border border-amber-200 bg-amber-50 p-3">
            <Label className="text-amber-900">Değişiklik Yorumu * (zorunlu)</Label>
            <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Örn: Kategori adı düzeltildi" data-testid="edit-trade-comment-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button onClick={save} disabled={saving} data-testid="edit-trade-save-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
